import React, { useMemo, useEffect } from "react";
import { useGetSingleMetric } from './queries/useGetSingleMetric';
import { useGetFieldValueCounts } from './queries/useGetFieldValueCounts';
import { useGetDataQuery, useES_CountData } from '@/utils/react-query-hooks/getData';
import { WIDGET_ELEM_ConfigForm } from './ConfigForm';
import { WIDGET_ELEM_processRawConfigFn } from './WIDGET_ELEM_processRawConfigFn';
import { WIDGET_ELEM_someReqPropsUnset } from './WIDGET_ELEM_someReqPropsUnset';
import { WidgetErrorState, WidgetLoadingState, WidgetEmptyState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { resolveDynamicSelection, getDynamicSelectionTitle, isNothingPicked } from "./utils/valueSelection";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { MetricCard } from "@/components/dataviz/MetricsCard";

// Helper to normalize selectedFieldValue to array (backward compatibility with legacy single values)
export const normalizeToArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value]; // Convert legacy single value to array
};

export const Component = ({
  contentType,
  field,
  statFunction,
  selectedFieldValue,
  categoricalStatFunction,
  measureMode,
  countType = 'document',
  displayMode,
  metricTitle,
  hasCustomTitle,
  // Value selection mode (see utils/valueSelection.js)
  fieldValuesVisibilityMode,
  fieldValuesDynamicPreset,
  fieldValuesVisibilityN,
  id,
  widgetIdx = 0,
  activeFilters = [],
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  prefetchedData
}) => {
  const isDocCount = measureMode === 'docCount';
  // Counting a dataset can mean two things: its documents, or the annotations
  // made on them. Only offered on a dataset pick (docCount), never on a field
  // statistic.
  const isAnnotationCount = isDocCount && countType === 'annotation';
  const isNumberInput = field?.comp === 'NumberInput';

  // The caption input (TitleInputWithDefault / useWidgetTitle) shows the field's
  // display name whenever no title is stored, but picking a categorical field
  // stores no metricTitle until a value is toggled (ConfigForm_Context /
  // ConfigForm_DataSection). Fall back to the same default here so the card
  // never renders without the label the form is showing.
  // In dynamic mode the manual path's auto-title ("State - A, B") is stale, so
  // derive one unless the user typed their own.
  const cardTitle =
    (fieldValuesVisibilityMode === "dynamic" && !isNumberInput && !isDocCount && !hasCustomTitle && field?.display)
      ? getDynamicSelectionTitle(field.display, fieldValuesDynamicPreset, fieldValuesVisibilityN)
      : (metricTitle || field?.display || undefined);

  // selectedFieldValue has three shapes for a categorical field:
  //   undefined  -> key never written (fresh pick, or legacy/large-tenant widgets
  //                 that deliberately omit the list): aggregate over every value.
  //   [ ...ids ] -> explicit selection.
  //   []         -> the user unchecked everything: nothing to aggregate, ask them
  //                 to pick. Must NOT fall through to the "every value" path.
  //
  // That last rule only holds for configs the Show/Hide picker actually wrote.
  // Until it landed (262e04832), [] was the ONLY way to say "every value" — the
  // old picker always wrote the array, so unchecking everything and selecting
  // everything both produced [], and the empty-list fallback below aggregated
  // over all of it. Reading [] as "nothing picked" regardless of provenance
  // therefore blanked every pre-existing card saved that way, plus callers that
  // still clear the array deliberately to mean "all". Requiring an explicit
  // manual mode keeps the new meaning for configs that opted into it and leaves
  // the original meaning for everything else, which is exactly the set that
  // cannot have meant "nothing picked" — the old UI could not express it.
  // Dynamic mode ignores the manual list entirely and picks the top / bottom N
  // values by count from whatever the current filters surface.
  const isDynamicSelection =
    !isNumberInput && !isDocCount && fieldValuesVisibilityMode === "dynamic";
  const noValueSelected =
    !isNumberInput && !isDocCount && !isDynamicSelection &&
    isNothingPicked(selectedFieldValue, fieldValuesVisibilityMode);
  const allActiveFilters = [...activeFilters, ...presetActiveFilters, ...widgetFilters];

  const { updateWidgetDataStore } = useReportLandingContext() || {};

  // Snapshot replay: prefetchedData carries { countResponse, fieldValueCounts, data }
  // — exactly the three query outputs this widget's render path consumes. When
  // present, skip every fetch and read from the captured slice instead.

  // --- All hooks called unconditionally (React rules of hooks) ---

  // docCount mode: fetch document count via useGetDataQuery
  const { data: countResponseFromApi, status: countStatusFromApi, error: countError } = useGetDataQuery({
    contentTypes: contentType ? [contentType] : [],
    useCountDAL: true,
    findQuery: { kp_published_status: 'published' },
    activeFilters: allActiveFilters,
    queryOptions: { enabled: isDocCount && !isAnnotationCount && !!contentType && !prefetchedData }
  });

  // docCount mode, annotation count: the annotations index scoped to this
  // dataset. Same source as the Annotation Explorer's "N Annotations" header
  // (ES_countData on contentTypes: ['annotations'], dataset applied as a
  // meta.contentType filter), so the two can never disagree.
  const {
    data: annoCountResponseFromApi,
    status: annoCountStatusFromApi,
    error: annoCountError,
  } = useES_CountData({
    contentTypes: ['annotations'],
    activeFilters: [
      ...allActiveFilters,
      {
        filterId: 'contentTypeFilter_contentType',
        // processActiveFilters reshapes with values.map(v => v.value), so the
        // filter carries {value} objects, not bare strings.
        values: [{ value: contentType }],
        target: { filterType: 'valuePathType', valuePath: 'meta.contentType' },
      },
    ],
    enabled: isAnnotationCount && !!contentType && !prefetchedData,
  });

  // fieldStat mode: categorical field value counts
  const { data: fieldValueCountsFromApi, status: fieldValueCountsStatusFromApi } = useGetFieldValueCounts({
    contentType,
    field,
    activeFilters: allActiveFilters,
    // Same vizCardId as the config form's value picker: while the card has no
    // filters the two bodies are identical, and a shared key lets react-query
    // serve both from one request instead of two.
    vizCardId: id,
    queryOptions: {
      enabled: !isDocCount && !isNumberInput && !!contentType && !!field && !prefetchedData
    }
  });

  // fieldStat mode: numeric field metric from API
  const shouldFetch = !prefetchedData && !isDocCount && !!(contentType && field && isNumberInput && statFunction);
  const fieldForApi = field?.isExpression
    ? { valuePath: field.valuePath, isExpression: true }
    : (field?.valuePath || field);

  const { data: dataFromApi, status: statusFromApi, error } = useGetSingleMetric({
    vizCardId: id,
    contentType,
    field: fieldForApi,
    statFunction: isNumberInput ? statFunction : undefined,
    activeFilters: allActiveFilters,
    queryOptions: { enabled: shouldFetch },
  });

  // Prefer captured slice when replaying a snapshot; else use live query output.
  const countResponse = prefetchedData ? prefetchedData.countResponse : countResponseFromApi;
  const countStatus = prefetchedData ? "success" : countStatusFromApi;
  const annoCountResponse = prefetchedData ? prefetchedData.annoCountResponse : annoCountResponseFromApi;
  const annoCountStatus = prefetchedData ? "success" : annoCountStatusFromApi;
  const fieldValueCounts = prefetchedData ? prefetchedData.fieldValueCounts : fieldValueCountsFromApi;
  const fieldValueCountsStatus = prefetchedData ? "success" : fieldValueCountsStatusFromApi;
  const data = prefetchedData ? prefetchedData.data : dataFromApi;
  const status = prefetchedData ? "success" : statusFromApi;

  // Track loading status — use the relevant status based on mode
  const trackingStatus = isAnnotationCount
    ? annoCountStatus
    : isDocCount
    ? countStatus
    : isNumberInput
      ? status
      : fieldValueCountsStatus;
  useUpdateLoadedWidgetsTracker({ status: trackingStatus, widgetIdx });

  // Push the combined per-mode query outputs to the central store so a public
  // snapshot can replay them. Shape captured here === shape read from
  // prefetchedData above, so the round-trip reconstructs the same view.
  useEffect(() => {
    if (trackingStatus === "success" && id) {
      updateWidgetDataStore && updateWidgetDataStore(id, { countResponse, annoCountResponse, fieldValueCounts, data });
    }
  }, [trackingStatus, id, countResponse, annoCountResponse, fieldValueCounts, data, updateWidgetDataStore]);

  // --- fieldStat mode data prep ---

  // Get the selected options data (includes count and percentage) for Multi/Single Select fields.
  //
  // Empty-selectedFieldValue fallback: when no explicit values are passed,
  // aggregate over every value present in the filter-scoped fieldValueCounts.
  // This lets callers omit selectedFieldValue when they want "all values the
  // active filters surface" without serializing a per-id list into the
  // widget config (e.g. MAD's city/national avg widgets, where the universe
  // is ~5K volunteers — the explicit list adds ~150KB to the report doc and
  // a 5K-element array to every configTransform pass for no semantic gain).
  //
  // Map-based lookup: the previous .find() implementation was O(N×M) where
  // N = selectedFieldValue.length and M = fieldValueCounts.length. At MAD
  // scale (both ~5K) that's 25M string comparisons per render. The Map
  // brings it to O(N+M) — same output, ~2,500× fewer comparisons.
  const selectedOptionsData = useMemo(() => {
    if (isNumberInput || !fieldValueCounts) return null;

    if (isDynamicSelection) {
      return resolveDynamicSelection(fieldValueCounts, fieldValuesDynamicPreset, fieldValuesVisibilityN);
    }

    const valuesArray = normalizeToArray(selectedFieldValue);
    if (valuesArray.length === 0) return fieldValueCounts;

    const countsByValue = new Map(
      fieldValueCounts.map(item => [String(item.value), item])
    );
    return valuesArray
      .map(val => countsByValue.get(String(val)))
      .filter(Boolean);
  }, [isNumberInput, selectedFieldValue, fieldValueCounts, isDynamicSelection, fieldValuesDynamicPreset, fieldValuesVisibilityN]);

  // Use data from API for NumberInput fields, or selectedOptionsData for Multi/Single Select fields
  const metricData = useMemo(() => {
    if (isNumberInput) {
      return data;
    } else {
      // Multi/Single Select fields, use data from fieldValueCounts
      // Aggregate counts and percentages across all selected values
      //
      // An explicit selection that the filters leave nothing of (picked Nakuru,
      // then filtered to Country=India) is a real answer: zero. Render 0, not a
      // blank card — the value picker is unfiltered, so this is a normal state.
      if (
        selectedOptionsData && selectedOptionsData.length === 0 &&
        normalizeToArray(selectedFieldValue).length > 0
      ) {
        return {
          value: 0,
          count: 0,
          percentage: 0,
          statFunction: categoricalStatFunction || 'sum',
          field,
          contentType
        };
      }
      if (selectedOptionsData && selectedOptionsData.length > 0) {
        const counts = selectedOptionsData.map(opt => opt.count || 0);
        const percentages = selectedOptionsData.map(opt => opt.percentage || 0);
        
        // Get stat function (default to 'sum' for backward compatibility)
        const statFunc = categoricalStatFunction || 'sum';
        
        let totalCount, totalPercentage;
        
        switch (statFunc) {
          case 'avg':
            totalCount = counts.reduce((a, b) => a + b, 0) / counts.length;
            totalPercentage = percentages.reduce((a, b) => a + b, 0) / percentages.length;
            break;
          case 'min':
            totalCount = Math.min(...counts);
            totalPercentage = Math.min(...percentages);
            break;
          case 'max':
            totalCount = Math.max(...counts);
            totalPercentage = Math.max(...percentages);
            break;
          case 'median': {
            const sortedCounts = [...counts].sort((a, b) => a - b);
            const sortedPercentages = [...percentages].sort((a, b) => a - b);
            const mid = Math.floor(sortedCounts.length / 2);
            totalCount = sortedCounts.length % 2 !== 0 ? sortedCounts[mid] : (sortedCounts[mid - 1] + sortedCounts[mid]) / 2;
            totalPercentage = sortedPercentages.length % 2 !== 0 ? sortedPercentages[mid] : (sortedPercentages[mid - 1] + sortedPercentages[mid]) / 2;
            break;
          }
          case 'range':
            totalCount = Math.max(...counts) - Math.min(...counts);
            totalPercentage = Math.max(...percentages) - Math.min(...percentages);
            break;
          case 'sum':
          default:
            totalCount = counts.reduce((a, b) => a + b, 0);
            totalPercentage = percentages.reduce((a, b) => a + b, 0);
            break;
        }
        
        return {
          value: totalCount,
          count: totalCount,
          percentage: totalPercentage,
          statFunction: statFunc,
          field,
          contentType
        };
      }
      return null;
    }
  }, [isNumberInput, data, selectedOptionsData, categoricalStatFunction, field, contentType]);

  // Does the resolved selection cover EVERY value the field has? Read off the
  // resolved list rather than the stored one, so it holds for all three shapes
  // at once: an absent selectedFieldValue (selectedOptionsData IS
  // fieldValueCounts), an explicit list naming every value (legacy configs the
  // form no longer writes), and dynamic mode with N at or above the value count.
  const allValuesSelected =
    !isNumberInput &&
    Array.isArray(fieldValueCounts) && fieldValueCounts.length > 0 &&
    Array.isArray(selectedOptionsData) &&
    selectedOptionsData.length === fieldValueCounts.length;

  // --- docCount mode render ---
  // Deliberately AFTER every hook in this component. measureMode can now flip
  // while the widget stays mounted (picking a dataset folder in the data picker
  // switches to docCount in one update), and returning above the useMemos above
  // renders fewer hooks than the previous pass — React throws on that.
  if (isAnnotationCount) {
    if (annoCountStatus === 'loading') return <WidgetLoadingState />;
    if (annoCountStatus === 'error') return <WidgetErrorState error={annoCountError} />;

    const annoCount = annoCountResponse?.count;
    if (annoCount == null) return null;

    return (
      <MetricCard
        display={annoCount.toLocaleString()}
        title={cardTitle}
        tagDisplay="Annotation Count"
        allActiveFilters={allActiveFilters}
        style={style}
      />
    );
  }

  if (isDocCount) {
    if (countStatus === 'loading') return <WidgetLoadingState />;
    if (countStatus === 'error') return <WidgetErrorState error={countError} />;

    const docCount = countResponse?.data?.[0]?.count;
    if (docCount == null) return null;

    return (
      <MetricCard
        display={docCount.toLocaleString()}
        title={cardTitle}
        tagDisplay="Document Count"
        allActiveFilters={allActiveFilters}
        style={style}
      />
    );
  }

  const formatPercentage = (percentage) => {
    if (percentage === undefined || percentage === null || isNaN(percentage)) return '';
    const rounded = Math.round(percentage * 10) / 10;
    return rounded === Math.round(rounded) 
      ? `${Math.round(rounded)}%` 
      : `${rounded.toFixed(1)}%`;
  };

  const formatMetricValue = () => {
    // Check if metricData exists first
    if (!metricData || metricData.value === undefined || metricData.value === null) {
      return "N/A";
    }
    
    // Multi/Single Select fields — format based on displayMode
    if (!isNumberInput && metricData.percentage !== undefined) {
      const mode = displayMode || 'countAndPercentage';
      const formattedCount = metricData.count.toLocaleString();

      // Every value selected AND the share covers the whole scope: the
      // percentage can then only restate that scope. A field whose values
      // partition the data (Topics — one annotation, one node) reads exactly
      // 100%; a multi-value one (tags, CheckboxList) counts a document once per
      // value it carries and reads PAST 100%. Neither says anything, so show
      // the count alone — in percentageOnly mode too, where the count is the
      // only honest number left.
      //
      // BELOW 100% it is not vacuous and stays: a document with no value for
      // the field gets no bucket (the dimension $unwind drops it, absent
      // includePartialDimensions), so "all values selected, 87%" reads "87% of
      // the documents in scope have any value for this field" — the field's
      // coverage, which is worth seeing. The threshold is the rounding
      // formatPercentage applies, so the test is exactly "would this have
      // printed 100%?".
      if (allValuesSelected && metricData.percentage >= 99.95) return formattedCount;

      const formattedPercentage = formatPercentage(metricData.percentage);

      if (mode === 'percentageOnly') return formattedPercentage;
      if (mode === 'countAndPercentage') return `${formattedCount} (${formattedPercentage})`;
      return formattedCount; // countOnly (default)
    }
    
    // the value based on stat function for NumberInput fields
    
    let displayValue = metricData.value;
    
    if (typeof displayValue === 'object' && displayValue !== null) {
      // Handle objects (e.g., from mode function or complex field values)
      if (displayValue.display) {
        // If object has a display property, use it
        displayValue = String(displayValue.display);
      } else if (displayValue.value !== undefined) {
        // If object has a value property, use it
        displayValue = String(displayValue.value);
      } else {
        // Otherwise stringify the object
        displayValue = JSON.stringify(displayValue);
      }
    } else if (typeof displayValue === 'number') {
      // Format numbers with appropriate precision (0 is a valid number)
      const currentStatFunction = metricData?.statFunction || statFunction || 'count';
      if (currentStatFunction === 'count' || currentStatFunction === 'uniqueCount' || currentStatFunction === 'outlierCount') {
        displayValue = Math.round(displayValue).toLocaleString();
      } else if (currentStatFunction === 'outlierPercent') {
        displayValue = `${displayValue.toFixed(1)}%`;
      } else {
        displayValue = displayValue.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      }
    } else {
      // Convert any other type to string
      displayValue = String(displayValue);
    }

    return displayValue;
  };

  const renderMetricCard = () => {
    const formattedValue = formatMetricValue();
    
    // Determine tagDisplay based on field type
    let tagDisplay;
    if (isNumberInput) {
      // For number fields: show the stat function name
      const currentStatFunction = metricData?.statFunction || statFunction || 'count';
      tagDisplay = currentStatFunction.charAt(0).toUpperCase() + currentStatFunction.slice(1);
    } else {
      // For non-number fields: "count" for single value, stat function name for
      // multiple values. Read the RESOLVED selection, not the stored list — an
      // absent selectedFieldValue means every value, and those do get aggregated.
      // Fall back to the stored list when the filters resolve it to nothing, so
      // the zero card above keeps the stat function it just applied.
      const selectedValuesCount =
        selectedOptionsData?.length || normalizeToArray(selectedFieldValue).length;
      if (selectedValuesCount <= 1) {
        tagDisplay = "Count";
      } else {
        const currentStatFunction = metricData?.statFunction || categoricalStatFunction || 'sum';
        tagDisplay = currentStatFunction.charAt(0).toUpperCase() + currentStatFunction.slice(1);
      }
    }
    
    return ( 
      <MetricCard
        display={formattedValue}
        title={cardTitle}
        tagDisplay={tagDisplay}
        allActiveFilters={allActiveFilters}
        style={style}
      />
    );
  };

  // For Multi/Single Select fields, check fieldValueCounts status
  if (!isNumberInput) {
    if (noValueSelected) {
      return <WidgetEmptyState display="Please select a value" />;
    }
    if (fieldValueCountsStatus === "loading") {
      return <WidgetLoadingState />;
    }
    if (fieldValueCountsStatus === "error") {
      return <WidgetErrorState error={error} />;
    }
    if (metricData) {
      return renderMetricCard();
    }
    return null;
  }

  // For NumberInput fields, check API status
  if (status === "loading") {
    return <WidgetLoadingState />;
  }

  if (status === "error") {
    return <WidgetErrorState error={error} />;
  }

  // Only render if we have valid metricData
  if (metricData) {
    return renderMetricCard();
  }

  return null;
};

export const optionDef = { 
  display: "Metric Card", 
  icon: "Hash", 
  value: "numberBasic", 
  comp: 'WidgetMetricCard' 
};

export const defaultConfig = { 
  props: {
    readOnly: { 
      disableHeader: true, 
    }
  }
};

export const processRawConfigFn = WIDGET_ELEM_processRawConfigFn;

export const ConfigForm = (props) => (
  <WIDGET_ELEM_ConfigForm 
    {...props}
    widgetIcon={optionDef.icon} 
    widgetTitle={optionDef.display} 
  />
);

export const someReqPropsUnset = WIDGET_ELEM_someReqPropsUnset;
